import { InfoBanner, WarningBanner } from 'ui/banner';
import { Divider } from 'ui/divider';
import { Table } from 'ui/table';
import type { ColumnData } from 'ui/tableHeader/constants';
import { TableRows } from 'ui/tableRows';
import { body2, spacing } from 'ui/theme';
import styled from 'styled-components';
import { IS_VALID_EMAIL } from 'utils/validationRegex';
import { CSVInputPreview } from './components/table/CSVInputPreview';

type RowContent = {
  row: {
    listProperty: string;
    propertyContent: string;
  };
};

interface Panel3Page {
  listName: string;
  listDescription: string;
  listType: string;
  triggerJourney: boolean;
  csvUploadInput: string;
  csvTextInput: string;
  csvFileName: string;
  csvFileSize: string;
  setRowCount: (value: number) => number;
}

export const ListPropertyCell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  text-align: left;
  font-weight: bold;
  width: 150px;
`;

export const PropertyContentCell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  text-align: left;
`;

const StyledInfoBanner = styled(InfoBanner)`
  margin-bottom: ${spacing.threeQuarter};
`;

const StyledWarningBanner = styled(WarningBanner)`
  margin-bottom: ${spacing.threeQuarter};
`;

const NewUserFields = styled(PropertyContentCell)`
  color: #59c1a7;
  font-weight: bold;
`;

export function Panel3Content({
  listName,
  listDescription,
  listType,
  triggerJourney,
  csvUploadInput,
  csvTextInput,
  csvFileName,
  csvFileSize,
  setRowCount,
}: Panel3Page) {
  const containsValidemail =
    (csvTextInput && !IS_VALID_EMAIL.test(csvTextInput.split(',')[0])) ||
    (csvUploadInput && !IS_VALID_EMAIL.test(csvUploadInput.split('\n')[0]));

  return (
    <div>
      <StyledInfoBanner>
        Please review your list. Remember, once you upload this list you will <strong>not</strong>{' '}
        be able to change the <code>user fields</code>. Fields are case sensitive.
      </StyledInfoBanner>

      {containsValidemail ? (
        <StyledWarningBanner>
          You must have a field called <strong>email</strong>
        </StyledWarningBanner>
      ) : (
        <div></div>
      )}

      <Table>
        <TableRows
          columns={[
            {
              name: 'ListProperty',
              value: 'listProperty',
              align: 'flex-start',
              sortable: false,
              RowCell: ({ row }: RowContent) => (
                <ListPropertyCell>{row.listProperty}</ListPropertyCell>
              ),
            } as ColumnData,
            {
              name: 'PropertyContent',
              value: 'propertyContent',
              align: 'flex-start',
              sortable: false,
              RowCell: ({ row }: RowContent) =>
                row.listProperty === 'New user fields to be added' ? (
                  <NewUserFields>{row.propertyContent}</NewUserFields>
                ) : (
                  <PropertyContentCell>{row.propertyContent}</PropertyContentCell>
                ),
            } as ColumnData,
          ]}
          rows={[
            {
              id: 1,
              listProperty: 'List name',
              propertyContent: listName,
            },
            {
              id: 2,
              listProperty: 'List description',
              propertyContent: listDescription,
            },
            {
              id: 3,
              listProperty: 'List type',
              propertyContent: listType,
            },
            {
              id: 4,
              listProperty: 'Trigger journeys',
              propertyContent: triggerJourney ? 'Yes' : 'No',
            },
          ].concat(
            csvTextInput
              ? [
                  {
                    id: 5,
                    listProperty: 'CSV source',
                    propertyContent: 'Textarea input',
                  },
                  {
                    id: 6,
                    listProperty: 'New user fields to be added',
                    propertyContent: csvTextInput.split('\n')[0].split(',').join(', '),
                  },
                ]
              : [
                  {
                    id: 7,
                    listProperty: 'File Name',
                    propertyContent: csvFileName,
                  },
                  {
                    id: 8,
                    listProperty: 'File Size',
                    propertyContent: csvFileSize,
                  },
                ],
          )}
          selectedRows={[]}
        />
      </Table>

      <Divider />
      <CSVInputPreview csvInput={csvTextInput || csvUploadInput} setRowCount={setRowCount} />
    </div>
  );
}
